import { Parser } from "hot-formula-parser";
import PROJECT_DETAILS_FORMULA_MAP from "../../constants/ProjectDetailsFormulaMap";
import { formatTemplate } from "../BRS/BRSUtils";
import { BulkEditRow } from "../../store/bulk-edit-store";

export interface FormulaResult {
  updatedFields: Record<string, string>; // fieldName → new calculated value
  errors: Record<string, string>;        // fieldName → error message
}

/**
 * Recalculates formula-dependent fields for a given row after a field value changes.
 * Returns the updated field values and any errors.
 */
export function recalculateFormulas(fieldName: string, row: BulkEditRow): FormulaResult {
  const updatedFields: Record<string, string> = {};
  const errors: Record<string, string> = {};
  const parser = new Parser();

  const sanitizedFieldName = fieldName.replace(/\s+/g, " ");

  // Check if this field triggers any formula recalculations
  if (!(sanitizedFieldName in PROJECT_DETAILS_FORMULA_MAP)) {
    return { updatedFields, errors };
  }

  const { resultFields } = PROJECT_DETAILS_FORMULA_MAP[sanitizedFieldName];
  if (!resultFields || resultFields.length === 0) {
    return { updatedFields, errors };
  }

  // Build a working copy of field values (using current row values + any already-calculated values)
  const fieldValues: Record<string, string> = {};
  for (const [name, data] of Object.entries(row.editableFields)) {
    fieldValues[name] = data.value;
  }

  // Process result fields in order (sequence matters for chained formulas)
  for (const resultField of resultFields) {
    const formulaDef = PROJECT_DETAILS_FORMULA_MAP[resultField];
    if (!formulaDef || !formulaDef.template || !formulaDef.params) {
      continue;
    }

    const { template, params } = formulaDef;

    // Load param values from current field values (including previously calculated ones)
    const loadParamValues = params.map((param) => {
      const val = fieldValues[param] || "0";
      return Number(val.replace(/,/g, ""));
    });

    // Format the template with param values
    const formattedTemplate = formatTemplate(template, ...loadParamValues);

    // Parse and calculate
    const { error, result } = parser.parse(formattedTemplate);

    if (error) {
      errors[resultField] = `Formula error: ${error}`;
    } else {
      const resultStr = String(result);
      updatedFields[resultField] = resultStr;
      // Update working copy for chained calculations
      fieldValues[resultField] = resultStr;
    }
  }

  return { updatedFields, errors };
}

/**
 * Checks if a field name is an input to any formula (i.e., has resultFields).
 */
export function isFormulaInput(fieldName: string): boolean {
  const sanitized = fieldName.replace(/\s+/g, " ");
  if (!(sanitized in PROJECT_DETAILS_FORMULA_MAP)) {
    return false;
  }
  const { resultFields } = PROJECT_DETAILS_FORMULA_MAP[sanitized];
  return resultFields && resultFields.length > 0;
}
